import { useEffect, useRef, useState, type JSX } from 'react';
import { useI18n } from '../i18n/context.js';

export interface ImageViewerItem {
  readonly src: string;
  readonly alt: string;
}

/**
 * Просмотр картинки во весь экран (E7-T2, §5.5).
 *
 * Нужен там, где превью намеренно маленькое: на листе персонажа четыре
 * ракурса, и решить «он или не он» по миниатюре в сто пикселей нельзя.
 *
 * Не `<dialog>`: `showModal` до сих пор нет в jsdom, и весь путь оказался бы
 * непокрытым тестами. Поэтому обычный слой с ручной обработкой Esc, возврата
 * фокуса и клика по фону — ровно то, что даёт `<dialog>`, но проверяемо.
 */
export function ImageViewer({
  items,
  initialIndex = 0,
  onClose,
}: {
  readonly items: readonly ImageViewerItem[];
  readonly initialIndex?: number;
  readonly onClose: () => void;
}): JSX.Element {
  const { t } = useI18n();
  const closeRef = useRef<HTMLButtonElement>(null);
  const [index, setIndex] = useState(() => clampIndex(initialIndex, items.length));
  const current = items[index];
  const hasCarousel = items.length > 1;

  const move = (delta: number): void => {
    setIndex((currentIndex) => (currentIndex + delta + items.length) % items.length);
  };

  useEffect(() => {
    const previous = document.activeElement;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (items.length > 1 && event.key === 'ArrowLeft') {
        event.preventDefault();
        setIndex((currentIndex) => (currentIndex - 1 + items.length) % items.length);
      } else if (items.length > 1 && event.key === 'ArrowRight') {
        event.preventDefault();
        setIndex((currentIndex) => (currentIndex + 1) % items.length);
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, [items.length, onClose]);

  if (!current) return <></>;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={current.alt}
      className="kitup-viewer"
      // Клик мимо картинки закрывает; клик по самой картинке — нет.
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button
        ref={closeRef}
        type="button"
        className="kitup-viewer-close"
        onClick={onClose}
        aria-label={t.common.close}
      >
        ×
      </button>
      {hasCarousel ? (
        <button
          type="button"
          className="kitup-viewer-arrow kitup-viewer-arrow-previous"
          aria-label={t.common.previousImage}
          onClick={() => move(-1)}
        >
          ‹
        </button>
      ) : null}
      <img src={current.src} alt={current.alt} className="kitup-viewer-image" />
      {hasCarousel ? (
        <>
          <button
            type="button"
            className="kitup-viewer-arrow kitup-viewer-arrow-next"
            aria-label={t.common.nextImage}
            onClick={() => move(1)}
          >
            ›
          </button>
          <p className="kitup-viewer-counter" role="status" aria-live="polite">
            {t.common.imageCount(index + 1, items.length)}
          </p>
        </>
      ) : null}
    </div>
  );
}

function clampIndex(index: number, length: number): number {
  if (length === 0) return 0;
  return Math.max(0, Math.min(index, length - 1));
}
